import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MEDICATION_CONCEPTS,
  MEDICATION_FORMS,
  DOSE_UNITS,
  FREQUENCY_TYPES,
  FOLATE_CONCEPTS,
  type MedicationConcept,
} from '@toxofollow/shared';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Button, Badge, Table, Field, inputClass } from '../components/ui.js';
import { Select, Textarea, FormGrid, QueryState, ErrorState } from '../components/form.js';

interface ScheduleSpec {
  frequencyType: string;
  times: string[];
  startDate: string;
  endDate?: string | null;
}
interface OrderRow {
  id: string;
  concept: MedicationConcept;
  brandName: string | null;
  localName: string | null;
  route: string | null;
  form: string;
  concentrationMgPerMl: string | null;
  doseAmount: string | null;
  doseUnit: string | null;
  doseMgPerKg: string | null;
  schedule: ScheduleSpec;
  approvalStatus: string;
  isAdjunct: boolean;
  startDate: string;
  endDate: string | null;
  parentInstructions: string | null;
  doctorNotes: string | null;
  safetyNotes: string | null;
  prescriber: string | null;
  discontinuationReason: string | null;
}

type FormMode = 'create' | 'edit' | 'copy';
interface DoseRuleRow {
  id: string;
  doseAmount: string;
  doseUnit: string;
  effectiveDate: string;
  endDate: string | null;
  reason: string | null;
  signedAt: string | null;
  isCurrent: boolean;
}

function approvalTone(s: string): 'ok' | 'warn' | 'danger' | 'info' {
  if (s === 'approved') return 'ok';
  if (s === 'pending_review' || s === 'draft') return 'warn';
  if (s === 'rejected' || s === 'discontinued') return 'danger';
  return 'info';
}

function scheduleSummary(s: ScheduleSpec, freqLabel: (k: string) => string): string {
  const times = s.times?.length ? ` @ ${s.times.join(', ')}` : '';
  return `${freqLabel(s.frequencyType)}${times}`;
}

export function Medications() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState<FormMode>('create');
  const [formInitial, setFormInitial] = useState<OrderRow | null>(null);
  const [reapprovalNotice, setReapprovalNotice] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const openCreate = () => {
    setReapprovalNotice(false);
    setFormMode('create');
    setFormInitial(null);
    setShowForm(true);
  };
  const openEdit = (o: OrderRow) => {
    setReapprovalNotice(false);
    setFormMode('edit');
    setFormInitial(o);
    setShowForm(true);
  };
  const openCopy = (o: OrderRow) => {
    setReapprovalNotice(false);
    setFormMode('copy');
    setFormInitial(o);
    setShowForm(true);
  };
  const closeForm = () => {
    setShowForm(false);
    setFormInitial(null);
    setFormMode('create');
  };

  const ordersQ = useQuery({ queryKey: ['medications', 'orders'], queryFn: () => api.get<{ orders: OrderRow[] }>('/medications/orders') });

  const approveM = useMutation({
    mutationFn: (id: string) => api.post(`/medications/orders/${id}/approve`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['medications'] }),
  });
  const suspendM = useMutation({
    mutationFn: (id: string) => api.post(`/medications/orders/${id}/suspend`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['medications'] }),
  });
  const discontinueM = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.post(`/medications/orders/${id}/discontinue`, { reason }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['medications'] }),
  });

  const freqLabel = (k: string) => t(`freq.${k}`) === `freq.${k}` ? k.replace(/_/g, ' ') : t(`freq.${k}`);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-slate-800">{t('med.title')}</h1>
        <Button onClick={() => (showForm ? closeForm() : openCreate())}>{showForm ? t('common.cancel') : `+ ${t('med.new')}`}</Button>
      </div>

      {reapprovalNotice && (
        <div className="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" role="status">
          {t('med.reapprovalNeeded')}
        </div>
      )}

      {showForm && (
        <OrderForm
          mode={formMode}
          initial={formInitial}
          onDone={(reapprovalNeeded) => {
            setReapprovalNotice(formMode === 'edit' && !!reapprovalNeeded);
            closeForm();
          }}
        />
      )}

      <Card title="">
        <QueryState
          isLoading={ordersQ.isLoading}
          isError={ordersQ.isError}
          error={ordersQ.error}
          errorFallback={t('common.error')}
          isEmpty={(ordersQ.data?.orders.length ?? 0) === 0}
          emptyMessage="—"
        >
          <div className="space-y-3">
            {ordersQ.data?.orders.map((o) => {
              const isFolate = FOLATE_CONCEPTS.includes(o.concept);
              return (
                <div key={o.id} className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <strong className="text-slate-800">{t(`concept.${o.concept}`)}</strong>
                        {isFolate && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-800" title={t('med.folateWarning')}>
                            {o.concept === 'folinic_acid' ? t('med.folinic') : t('med.folic')}
                          </span>
                        )}
                        {o.brandName && <span className="text-slate-400 text-sm">({o.brandName})</span>}
                        <Badge tone={approvalTone(o.approvalStatus)}>{t(`approval.${o.approvalStatus}`)}</Badge>
                        {o.isAdjunct && <Badge tone="info">{t('meds.adjunct')}</Badge>}
                      </div>
                      <div className="text-sm text-slate-600 mt-1">
                        {t(`form.${o.form}`)} · {o.doseAmount ? `${o.doseAmount} ${o.doseUnit ?? ''}` : '—'} · {scheduleSummary(o.schedule, freqLabel)}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {o.startDate}{o.endDate ? ` → ${o.endDate}` : ''}
                        {o.discontinuationReason ? ` · ${o.discontinuationReason}` : ''}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {(o.approvalStatus === 'draft' || o.approvalStatus === 'pending_review' || o.approvalStatus === 'suspended') && (
                        <Button variant="primary" onClick={() => approveM.mutate(o.id)} disabled={approveM.isPending}>{t('common.approve')}</Button>
                      )}
                      <Button variant="secondary" onClick={() => openEdit(o)}>{t('common.edit')}</Button>
                      <Button variant="secondary" onClick={() => openCopy(o)}>{t('med.copy')}</Button>
                      {o.approvalStatus === 'approved' && (
                        <Button variant="secondary" onClick={() => suspendM.mutate(o.id)} disabled={suspendM.isPending}>{t('med.suspend')}</Button>
                      )}
                      {o.approvalStatus !== 'discontinued' && (
                        <Button
                          variant="danger"
                          onClick={() => {
                            const reason = window.prompt(t('med.reasonPrompt'));
                            if (reason) discontinueM.mutate({ id: o.id, reason });
                          }}
                        >
                          {t('med.discontinue')}
                        </Button>
                      )}
                      <Button variant="ghost" onClick={() => setExpanded((e) => (e === o.id ? null : o.id))}>
                        {expanded === o.id ? '▲' : '▼'} {t('med.doseHistory')}
                      </Button>
                    </div>
                  </div>
                  {expanded === o.id && <DoseRuleHistory orderId={o.id} />}
                </div>
              );
            })}
          </div>
        </QueryState>
        {(approveM.isError || suspendM.isError || discontinueM.isError) && (
          <div className="mt-2"><ErrorState error={approveM.error ?? suspendM.error ?? discontinueM.error} fallback={t('common.error')} /></div>
        )}
      </Card>
    </div>
  );
}

function DoseRuleHistory({ orderId }: { orderId: string }) {
  const { t } = useI18n();
  const q = useQuery({
    queryKey: ['medications', 'dose-rules', orderId],
    queryFn: () => api.get<{ doseRules: DoseRuleRow[] }>(`/medications/orders/${orderId}/dose-rules`),
  });
  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      <QueryState
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        errorFallback={t('common.error')}
        isEmpty={(q.data?.doseRules.length ?? 0) === 0}
        emptyMessage="—"
      >
        <Table headers={[t('med.dose'), t('weekly.weekOf'), t('med.signed'), t('med.reason')]}>
          {q.data?.doseRules.map((dr) => (
            <tr key={dr.id} className="border-b border-slate-100">
              <td className="py-1 pr-4">
                {dr.doseAmount} {dr.doseUnit} {dr.isCurrent && <Badge tone="ok">{t('med.current')}</Badge>}
              </td>
              <td className="py-1 pr-4">{dr.effectiveDate}{dr.endDate ? ` → ${dr.endDate}` : ''}</td>
              <td className="py-1 pr-4 text-xs text-slate-400">{dr.signedAt ? dr.signedAt.slice(0, 10) : '—'}</td>
              <td className="py-1 pr-4 text-slate-500">{dr.reason || '—'}</td>
            </tr>
          ))}
        </Table>
      </QueryState>
    </div>
  );
}

function OrderForm({
  mode = 'create',
  initial = null,
  onDone,
}: {
  mode?: FormMode;
  initial?: OrderRow | null;
  onDone: (reapprovalNeeded?: boolean) => void;
}) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  // Prefill from `initial` for edit/copy; sensible defaults for create.
  const [concept, setConcept] = useState<string>(initial?.concept ?? 'pyrimethamine');
  const [brandName, setBrandName] = useState(initial?.brandName ?? '');
  const [localName, setLocalName] = useState(initial?.localName ?? '');
  const [form, setForm] = useState<string>(initial?.form ?? 'suspension');
  const [doseAmount, setDoseAmount] = useState(initial?.doseAmount ?? '');
  const [doseUnit, setDoseUnit] = useState<string>(initial?.doseUnit ?? 'mg');
  const [doseMgPerKg, setDoseMgPerKg] = useState(initial?.doseMgPerKg ?? '');
  const [concentrationMgPerMl, setConcentration] = useState(initial?.concentrationMgPerMl ?? '');
  const [frequencyType, setFrequencyType] = useState<string>(initial?.schedule?.frequencyType ?? 'once_daily');
  const [times, setTimes] = useState((initial?.schedule?.times ?? ['08:00']).join(', '));
  const [startDate, setStartDate] = useState(initial?.startDate ?? today);
  const [endDate, setEndDate] = useState(initial?.endDate ?? '');
  const [isAdjunct, setIsAdjunct] = useState(initial?.isAdjunct ?? false);
  const [parentInstructions, setParentInstructions] = useState(initial?.parentInstructions ?? '');
  const [doctorNotes, setDoctorNotes] = useState(initial?.doctorNotes ?? '');
  const [safetyNotes, setSafetyNotes] = useState(initial?.safetyNotes ?? '');
  const [prescriber, setPrescriber] = useState(initial?.prescriber ?? '');

  // Build the medicationOrderInput body shared by create/edit/copy.
  // SAFETY: `concept` is whatever the clinician explicitly chose — never auto-
  // substituted between folinic_acid and folic_acid. Decimals stay strings.
  const buildBody = () => ({
    concept,
    brandName: brandName.trim() || undefined,
    localName: localName.trim() || undefined,
    // Preserve the existing route on edit/copy; default oral for new orders.
    route: initial?.route ?? 'oral',
    form,
    concentrationMgPerMl: concentrationMgPerMl.trim() || undefined,
    doseAmount: doseAmount.trim() || undefined,
    doseUnit: doseAmount.trim() ? doseUnit : undefined,
    doseMgPerKg: doseMgPerKg.trim() || undefined,
    schedule: {
      frequencyType,
      times: times.split(',').map((x) => x.trim()).filter(Boolean),
      startDate,
      endDate: endDate.trim() || undefined,
    },
    startDate,
    endDate: endDate.trim() || undefined,
    isAdjunct,
    parentInstructions: parentInstructions.trim() || undefined,
    doctorNotes: doctorNotes.trim() || undefined,
    safetyNotes: safetyNotes.trim() || undefined,
    prescriber: prescriber.trim() || undefined,
  });

  const m = useMutation({
    mutationFn: async () => {
      const body = buildBody();
      if (mode === 'edit' && initial) {
        // Editing a clinical field of an approved order returns it to
        // pending_review on the server; approval stays a separate action.
        return api.put<{ ok: boolean; reapprovalNeeded: boolean }>(`/medications/orders/${initial.id}`, body);
      }
      if (mode === 'copy' && initial) {
        // Produces a fresh DRAFT from the source order + these overrides.
        return api.post<{ order: OrderRow }>(`/medications/orders/${initial.id}/copy`, body);
      }
      // create: new orders enter as draft and are never self-approved.
      return api.post('/medications/orders', { ...body, approvalStatus: 'draft' });
    },
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['medications'] });
      const reapprovalNeeded =
        res && typeof res === 'object' && 'reapprovalNeeded' in res ? Boolean((res as { reapprovalNeeded?: boolean }).reapprovalNeeded) : false;
      onDone(reapprovalNeeded);
    },
  });

  const cardTitle = mode === 'edit' ? t('med.editTitle') : mode === 'copy' ? t('med.copyTitle') : `+ ${t('med.new')}`;

  return (
    <Card title={cardTitle}>
      {mode !== 'edit' && <p className="text-xs text-slate-500 mb-3">{t('med.draftNotice')}</p>}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <FormGrid>
          <Field label={t('med.concept')}>
            <Select value={concept} onChange={(e) => setConcept(e.target.value)}>
              {MEDICATION_CONCEPTS.map((c) => (
                <option key={c} value={c}>{t(`concept.${c}`)}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('med.brandName')}>
            <input className={inputClass} value={brandName} onChange={(e) => setBrandName(e.target.value)} placeholder="Fansidar, Adiazine…" />
          </Field>
          <Field label={t('med.localName')}>
            <input className={inputClass} value={localName} onChange={(e) => setLocalName(e.target.value)} />
          </Field>
          <Field label={t('med.form')}>
            <Select value={form} onChange={(e) => setForm(e.target.value)}>
              {MEDICATION_FORMS.map((f) => (
                <option key={f} value={f}>{t(`form.${f}`)}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('med.concentration')}>
            <input className={inputClass} inputMode="decimal" value={concentrationMgPerMl} onChange={(e) => setConcentration(e.target.value)} />
          </Field>
          <Field label={t('med.doseAmount')}>
            <input className={inputClass} inputMode="decimal" value={doseAmount} onChange={(e) => setDoseAmount(e.target.value)} />
          </Field>
          <Field label={t('med.doseUnit')}>
            <Select value={doseUnit} onChange={(e) => setDoseUnit(e.target.value)}>
              {DOSE_UNITS.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('med.doseMgPerKg')}>
            <input className={inputClass} inputMode="decimal" value={doseMgPerKg} onChange={(e) => setDoseMgPerKg(e.target.value)} />
          </Field>
          <Field label={t('med.frequency')}>
            <Select value={frequencyType} onChange={(e) => setFrequencyType(e.target.value)}>
              {FREQUENCY_TYPES.map((f) => (
                <option key={f} value={f}>{t(`freq.${f}`)}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('med.times')}>
            <input className={inputClass} value={times} onChange={(e) => setTimes(e.target.value)} placeholder="08:00, 20:00" />
          </Field>
          <Field label={t('med.startDate')}>
            <input className={inputClass} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
          </Field>
          <Field label={t('med.endDate')}>
            <input className={inputClass} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Field>
          <Field label={t('med.prescriber')}>
            <input className={inputClass} value={prescriber} onChange={(e) => setPrescriber(e.target.value)} />
          </Field>
        </FormGrid>
        <label className="flex items-center gap-2 text-sm mb-3">
          <input type="checkbox" checked={isAdjunct} onChange={(e) => setIsAdjunct(e.target.checked)} />
          {t('meds.adjunct')}
        </label>
        <Field label={t('med.parentInstructions')}>
          <Textarea rows={2} value={parentInstructions} onChange={(e) => setParentInstructions(e.target.value)} />
        </Field>
        <Field label={t('med.doctorNotes')}>
          <Textarea rows={2} value={doctorNotes} onChange={(e) => setDoctorNotes(e.target.value)} />
        </Field>
        <Field label={t('med.safetyNotes')}>
          <Textarea rows={2} value={safetyNotes} onChange={(e) => setSafetyNotes(e.target.value)} />
        </Field>
        {m.isError && <div className="mb-3"><ErrorState error={m.error} fallback={t('common.error')} /></div>}
        <div className="flex gap-2">
          <Button type="submit" disabled={m.isPending}>{t('common.save')}</Button>
          <Button type="button" variant="secondary" onClick={() => onDone()}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}
